import { useRef, useCallback, useEffect, useMemo } from 'react';
import { debounce, throttle, RateLimiter } from '../utils/rateLimiter';

/**
 * Hook for debounced functions
 * 
 * @param {Function} func - Function to debounce
 * @param {number} wait - Milliseconds to wait
 * @param {Object} options - Debounce options
 * @returns {Function} - Debounced function
 * 
 * @example
 * const handleSearch = useDebounce((query) => {
 *   searchAPI(query);
 * }, 300);
 */
export const useDebounce = (func, wait = 300, options = {}) => {
  const funcRef = useRef(func);
  
  // Update ref when func changes
  useEffect(() => {
    funcRef.current = func;
  }, [func]);
  
  // Create debounced function
  const debouncedFunc = useMemo(
    () => debounce((...args) => funcRef.current(...args), wait, options),
    [wait, options.leading, options.trailing, options.maxWait]
  );
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedFunc.cancel();
    };
  }, [debouncedFunc]);
  
  return debouncedFunc;
};

/**
 * Hook for throttled functions
 * 
 * @param {Function} func - Function to throttle
 * @param {number} wait - Milliseconds to wait
 * @param {Object} options - Throttle options
 * @returns {Function} - Throttled function
 * 
 * @example
 * const handleScroll = useThrottle(() => {
 *   checkScrollPosition();
 * }, 100);
 */
export const useThrottle = (func, wait = 300, options = {}) => {
  const funcRef = useRef(func);
  
  useEffect(() => {
    funcRef.current = func;
  }, [func]);
  
  const throttledFunc = useMemo(
    () => throttle((...args) => funcRef.current(...args), wait, options),
    [wait, options.leading, options.trailing]
  );
  
  useEffect(() => {
    return () => {
      throttledFunc.cancel();
    };
  }, [throttledFunc]);
  
  return throttledFunc;
};

/**
 * Hook for rate limiting with state management
 * 
 * @param {number} maxCalls - Maximum calls per window
 * @param {number} windowMs - Time window in milliseconds
 * @returns {Object} - { tryAcquire, getRemainingCalls, getTimeUntilNextCall, isRateLimited }
 * 
 * @example
 * const { tryAcquire, getRemainingCalls } = useRateLimiter(5, 60000);
 * 
 * const handleSubmit = async () => {
 *   if (!tryAcquire()) {
 *     alert('Too many requests!');
 *     return;
 *   }
 *   await submitForm();
 * };
 */
export const useRateLimiter = (maxCalls = 10, windowMs = 60000) => {
  const limiterRef = useRef(null);
  
  if (!limiterRef.current) {
    limiterRef.current = new RateLimiter(maxCalls, windowMs);
  }
  
  const tryAcquire = useCallback(() => {
    return limiterRef.current.tryAcquire();
  }, []);
  
  const getRemainingCalls = useCallback(() => {
    return limiterRef.current.getRemainingCalls();
  }, []);
  
  const getTimeUntilNextCall = useCallback(() => {
    return limiterRef.current.getTimeUntilNextCall();
  }, []);
  
  const reset = useCallback(() => {
    limiterRef.current.reset();
  }, []);
  
  const isRateLimited = useCallback(() => {
    return limiterRef.current.getRemainingCalls() === 0;
  }, []);
  
  return {
    tryAcquire,
    getRemainingCalls,
    getTimeUntilNextCall,
    reset,
    isRateLimited,
  };
};

/**
 * Hook for rate-limited async functions
 * Returns a wrapped function that respects rate limits
 * 
 * @param {Function} func - Async function to rate limit
 * @param {number} maxCalls - Maximum calls per window
 * @param {number} windowMs - Time window in milliseconds
 * @returns {Object} - { execute, getRemainingCalls, isRateLimited, error }
 * 
 * @example
 * const { execute, getRemainingCalls, error } = useRateLimitedFunction(
 *   async (data) => await submitToAPI(data),
 *   3,
 *   60000
 * );
 * 
 * const handleSubmit = async () => {
 *   try {
 *     await execute(formData);
 *   } catch (err) {
 *     console.error(err);
 *   }
 * };
 */
export const useRateLimitedFunction = (func, maxCalls = 10, windowMs = 60000) => {
  const limiterRef = useRef(null);
  const errorRef = useRef(null);
  
  if (!limiterRef.current) {
    limiterRef.current = new RateLimiter(maxCalls, windowMs);
  }
  
  const execute = useCallback(async (...args) => {
    if (!limiterRef.current.tryAcquire()) {
      const timeUntilNext = limiterRef.current.getTimeUntilNextCall();
      const error = new Error(
        `Rate limit exceeded. Please wait ${Math.ceil(timeUntilNext / 1000)} seconds before trying again.`
      );
      error.rateLimited = true;
      error.retryAfter = timeUntilNext;
      errorRef.current = error;
      throw error;
    }
    
    errorRef.current = null;
    return await func(...args);
  }, [func]);
  
  const getRemainingCalls = useCallback(() => {
    return limiterRef.current.getRemainingCalls();
  }, []);
  
  const isRateLimited = useCallback(() => {
    return limiterRef.current.getRemainingCalls() === 0;
  }, []);
  
  const reset = useCallback(() => {
    limiterRef.current.reset();
    errorRef.current = null;
  }, []);
  
  return {
    execute,
    getRemainingCalls,
    isRateLimited,
    reset,
    error: errorRef.current,
  };
};

export default useRateLimiter;

